import { useState } from 'react';
import { isImageAvatar, getInitials } from '../utils/avatarUtils';

interface AvatarDisplayProps {
    avatar: string | null | undefined;
    username: string;
    size?: number;
    className?: string;
    style?: React.CSSProperties;
    /** Border radius — defaults to '50%' (circle). Use a number for px. */
    borderRadius?: string | number;
}

/**
 * Reusable avatar component that correctly renders all three avatar formats:
 * 1. Firebase Storage URLs (https://...)
 * 2. Base64 data URIs (data:image/...)
 * 3. Emoji strings (short text like "👨")
 * 4. null/undefined → initials fallback
 *
 * If an image URL fails to load, gracefully falls back to initials.
 */
export default function AvatarDisplay({
    avatar,
    username,
    size = 36,
    className,
    style,
    borderRadius = '50%',
}: AvatarDisplayProps) {
    const [imgError, setImgError] = useState(false);

    const baseStyle: React.CSSProperties = {
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        borderRadius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        ...style,
    };

    // Case 1: Image avatar (URL or base64) that hasn't errored
    if (isImageAvatar(avatar) && !imgError) {
        return (
            <img
                src={avatar!}
                alt={username}
                className={className}
                style={{
                    ...baseStyle,
                    objectFit: 'cover',
                }}
                onError={() => setImgError(true)}
            />
        );
    }

    // Case 2: Emoji avatar (short string, not a URL)
    if (avatar && !isImageAvatar(avatar) && avatar.length <= 4) {
        return (
            <div
                className={className}
                style={{
                    ...baseStyle,
                    background: 'var(--accent-gradient)',
                    fontSize: size * 0.55,
                    lineHeight: 1,
                    color: 'white',
                    userSelect: 'none',
                }}
            >
                {avatar}
            </div>
        );
    }

    // Case 3: No avatar / broken image → initials fallback
    return (
        <div
            className={className}
            style={{
                ...baseStyle,
                background: 'var(--accent-gradient)',
                color: 'white',
                fontWeight: 700,
                fontSize: size * 0.38,
                fontFamily: 'Outfit, sans-serif',
                letterSpacing: 0.5,
                userSelect: 'none',
            }}
        >
            {getInitials(username)}
        </div>
    );
}
